import { memo } from 'react';
import { App, Button, Image } from 'antd';
import { ExportOutlined, CopyOutlined, PhoneOutlined } from '@ant-design/icons';
import { MessageBox } from 'react-chat-elements';
import { groupBy, isEmpty, TagColorStyle } from '@haina/utils-commons';
import { parseSimpleMarkdown } from '@/channel/bubbleMsgUtils';
import useConversationStore from '@/stores/ConversationStore';
import { useShallow } from 'zustand/react/shallow';
import { ReplyIcon } from '@/components/Icons';
import ChannelLogo from './ChannelLogo';
import { ERROR_IMG } from '@/config';

const outboundStyle = {
  'waba': { color: '#ccd4ae' },
  'whatsapp': { color: '#d9fdd3' },
  'wai': { color: '#d9fdd3' },
}

const BubbleIM = ({ handlePreview, handleContactClick, setNewChatModalVisible, setNewChatFormValues, scrollToMessage, focusMsg, ...message }) => {
  const { message: appMessage } = App.useApp();
  const setReferenceMsg = useConversationStore(useShallow((state) => state.setReferenceMsg));

  const openNewChatModal = ({ wa_id, wa_name }) => {
    setNewChatModalVisible(true);
    setNewChatFormValues((prev) => ({ ...prev, phone_number: wa_id, name: wa_name }));
  };
  // Render parsed tokens to React elements
  const renderMDTokens = (tokens) => {
    return tokens.map((token, index) => {
      switch (token.type) {
        case 'text':
          return <span key={index}>{token.content}</span>
        case 'bold':
          return <b key={index}>{renderMDTokens(token.content)}</b>
        case 'italic':
          return <i key={index}>{renderMDTokens(token.content)}</i>
        case 'url':
          return (
            <a key={index} href={token.content} target='_blank' rel='noopener noreferrer' className=' underline text-sm'>
              {token.content}
            </a>
          )
        case 'number':
          return (
            <a key={`${index}`} className='text-sm ' onClick={() => openNewChatModal({ wa_id: token.content, wa_name: token.content })}>
              {token.content}
            </a>
          )
        default:
          return <span key={index}>{token.content}</span>
      }
    })
  };

  const RenderText = memo(function renderText({ str, className, template, message }) {
    let headerObj, footerObj, buttonsArr;
    if (!isEmpty(template) && !isEmpty(template.components)) {
      const componentsObj = groupBy(template.components.concat(template?.components_omit || []), (item) => item.type);
      headerObj = componentsObj?.header?.[0];
      footerObj = componentsObj?.footer?.[0];
      buttonsArr = componentsObj?.button; // ?.reduce((r, c) => r.concat(c.buttons), []);
    }

    return (
      <div className={`text-sm leading-5 emoji-text whitespace-pre-wrap ${className}`} key={'msg-text'}>
        {headerObj ? (
          <div className='text-neutral-500 text-center'>
            {'text' === (headerObj?.parameters?.[0]?.type || '').toLowerCase() && <div>{headerObj.text}</div>}
            {'image' === (headerObj?.parameters?.[0]?.type || '').toLowerCase() && <Image src={headerObj.parameters[0].image.link} height={100} fallback={ERROR_IMG}></Image>}
            {['document', 'video'].includes((headerObj?.parameters?.[0]?.type || '').toLowerCase()) && (
              <a href={headerObj.parameters[0][headerObj.parameters[0].type].link} target='_blank' key={headerObj.format} rel='noreferrer' className='text-sm'>
                [&nbsp;{headerObj.parameters[0].type}&nbsp;]
              </a>
            )}
          </div>
        ) : null}
        {renderMDTokens(parseSimpleMarkdown(str))}
        {footerObj ? <div className=' text-neutral-500'>{footerObj.text}</div> : null}
        {buttonsArr && buttonsArr.length > 0 ? (
          <div className='flex flex-row gap-1'>
            {buttonsArr.map((btn, index) =>
              btn.sub_type.toLowerCase() === 'url' ? (
                <Button className='text-blue-500' size={'small'} href={btn.url} target={'_blank'} key={`${btn.sub_type}_${btn.index}`} rel='noreferrer' icon={<ExportOutlined />}>
                  {btn.text}
                </Button>
              ) : btn.sub_type.toLowerCase() === 'phone_number' ? (
                <Button className='text-blue-500' size={'small'} key={btn.phone_number} rel='noreferrer' icon={<PhoneOutlined />}>
                  {btn.text} ({btn.phone_number})
                </Button>
              ) : (
                <Button className='text-blue-500' size={'small'} key={`${btn.type}_${btn.sub_type}_${btn.index}`} icon={btn.sub_type.toLowerCase() === 'copy_code' ? <CopyOutlined /> : btn.sub_type.toLowerCase() === 'quick_reply' ? <ReplyIcon /> : null}>
                  {btn.text || btn.sub_type.toUpperCase()}
                </Button>
              ),
            )}
          </div>
        ) : null}
      </div>
    )
  });
  return (
    <MessageBox
      {...message} titleColor={TagColorStyle(message.title).color}
      key={`IM.${message.id}`}
      position={message.sender === 'me' ? 'right' : 'left'}
      onReplyClick={() => setReferenceMsg(message)}
      onReplyMessageClick={() => scrollToMessage(message.reply.id)}
      onOpen={() => handlePreview(message)}
      onTitleClick={() => handlePreview(message)}
      // title={<div className='flex justify-around items-center gap-1'><WABIcon />{message.title}</div>}
      text={<RenderText str={message?.text || ''} className={message.status === 'failed' ? 'line-through text-neutral-400' : ''} template={message.template} message={message} />}
      replyButton={['text', 'document', 'image'].includes(message.whatsapp_msg_type) && message.replyButton}
      {...(message.sender === 'me'
        ? {
            // styles: { backgroundColor: '#ccd4ae' },
            notchStyle: { fill: outboundStyle[message.msg_source.toLowerCase()].color },
            title: <><ChannelLogo channel={message.msg_source} />{message.wabaName ? ` ${message.wabaName} - ${message.title || ''}` : ` ${message.title || message.from || ''}`}</>,
          }
        : {
          // title: <>&nbsp;<ChannelLogo channel={message.msg_source} />&nbsp;{message.title}</>,
          dateString: `${message.wabaName} - ${message.dateString}`,
        })}
      className={[
        'whitespace-pre-wrap', '[&_.rce-mbox-reply-message]:line-clamp-3',
        message.whatsapp_msg_type === 'sticker' ? 'bg-transparent' : '',
        // message.sender === 'me' ? 'whatsappme-container' : '',
        focusMsg === message.id ? 'message-box-focus' : '',
        message.status === 'failed' ? 'failed-msg' : '',
        // '*:bg-waba-me'
        message.sender === 'me' ? (message.msg_source.toLowerCase() === 'waba' ? `[&_.rce-mbox]:bg-waba-me` : `[&_.rce-mbox]:bg-whatsapp-me`) : '',
        message.sender !== 'me' ? (message.msg_source.toLowerCase() === 'waba' ? `[&_.rce-mbox-time:before]:text-waba-600 [&_.rce-mbox-time:before]:font-semibold` : `[&_.rce-mbox-time:before]:text-whatsapp`) : '',
      ].join(' ')}
      {...(message.type === 'meetingLink'
        ? {
            actionButtons: [
              ...(message.waBtn
                ? [
                    {
                      onClickButton: () => handleContactClick(message.data),
                      Component: () => <div key={'talk-now'}>发消息</div>,
                    },
                  ]
                : []),
              {
                onClickButton: () => {
                  navigator.clipboard.writeText(message.text);
                  appMessage.success('复制成功😀');
                },
                Component: () => <div key={'copy'}>复制</div>,
              },
            ],
          }
        : {})}
    />
  );
};
export default BubbleIM;
